///
/// EarthBrowser
///
/// Created by Matt Giger on 10/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useDockItems } from "@/components/windows/useDockItems"
import { useIsMobile } from "@/components/utils/use-mobile"
import { setViewportInsets } from "@/earthbrowser/utils/viewportInsets"
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets"
import { useAppSelector } from "@/redux/store"
import { useEffect, useSyncExternalStore } from "react"

/** CSS custom properties the TimelineBar publishes with its rendered size (px) */
export const cTimeControlHeightVar = "--eb-time-control-height"
export const cTimeControlWidthVar = "--eb-time-control-width"

/** Legend tray column width (Tailwind w-56); the time bar keeps clear of it */
export const cLegendTrayWidth = 224
/**
 * CSS custom property the LegendTray publishes: its rendered height plus
 * a gap (px) while it shows anything, else 0. The desktop Layers panel
 * ends above it rather than pushing the tray right
 */
export const cLegendTrayLiftVar = "--eb-legend-tray-lift"

/** Right dock geometry shared with RightDock so the chrome insets match */
export const cDockRailWidth = 48
export const cDockEdgeInset = 8

/** Desktop Layers panel geometry shared with LayersPanel so the left inset matches */
export const cLayersPanelWidth = 320
export const cLayersPanelEdgeInset = 16
/**
 * Height the rail leaves free at the bottom of the right edge for the
 * camera stack (compass + five 36px buttons + divider ≈ 230px, plus its
 * 24px bottom inset and a gap), so the stack can stay pinned to the edge
 */
export const cDockRailBottomReserve = 288
// Narrow enough for a laptop beside the Layers panel; wide enough that the
// essential three columns of the dataset tables fit without truncating
export const cMinDockWidth = 380
const cMaxDockWidthRatio = 0.7
// Globe width to keep clear of the dock: room for the time control (which
// the TimelineBar keeps at least 400px from the left edge, clearing the
// legends) plus half its width; in narrower windows the dock panel gives
// way first
const cMinGlobeWidth = 640

/** Panel width the right dock actually renders at for a given viewport */
export function effectiveDockWidth(persistedWidth: number, viewportWidth: number): number {
	const max = Math.min(
		viewportWidth * cMaxDockWidthRatio,
		viewportWidth - cDockRailWidth - cDockEdgeInset * 3 - cMinGlobeWidth
	)
	return Math.max(cMinDockWidth, Math.min(persistedWidth, max))
}

function subscribeResize(onChange: () => void) {
	window.addEventListener("resize", onChange)
	return () => window.removeEventListener("resize", onChange)
}

export function useViewportWidth(): number {
	return useSyncExternalStore(subscribeResize, () => window.innerWidth, () => 1920)
}

export interface ChromeInsets {
	/** Distance (px) from the viewport bottom for bottom-anchored chrome */
	bottom: number
	/**
	 * Width (px) of the right edge the data dock column occupies (rail +
	 * panel). For chrome that spans the height (time bar centering, the
	 * engine's view-axis shift); the camera stack ignores it because the
	 * rail leaves the bottom-right corner free
	 */
	right: number
	/**
	 * Extra distance (px) from the viewport left while the desktop Layers
	 * panel is open. For chrome that spans the height (the cutout HUDs,
	 * the time bar's centering); the legend tray ignores it, as the panel
	 * ends above the tray instead
	 */
	left: number
	/** Canvas height (px) the bottom dock covers; 0 when it isn't showing */
	coverBottom: number
}

/**
 * How far persistent chrome must stay from the viewport edges to clear
 * the data dock and the Layers panel: the time control, camera stack and
 * legend tray take `bottom` (24px, or clearance for the phone sheet when
 * it has tabs) and, on desktop, the time control and cutout HUDs shift
 * by `right` for the dock on the right edge and by `left` for the open
 * Layers panel. One source so the surfaces always line up.
 */
export function useChromeInsets(): ChromeInsets {
	const isMobile = useIsMobile()
	const bottomDrawer = useAppSelector(state => state.dialog.bottomDrawer)
	const layersPanelOpen = useAppSelector(state => state.dialog.layersPanelOpen)
	const dockWidth = useAppSelector(state => state.userInterface.bottomDrawer.dockWidth)
	const dockItems = useDockItems()
	const viewportWidth = useViewportWidth()
	const safeArea = useSafeAreaInsets()

	// Desktop docks on the right; phones get the bottom sheet
	const rightDock = !isMobile
	const bottomVisible = isMobile && bottomDrawer.tabs.length > 0

	const coverBottom = bottomVisible ? (bottomDrawer.isOpen ? bottomDrawer.height : 40) : 0
	// Clear the home indicator (native apps and notched browsers): the phone
	// sheet adds the pad below its content height, so chrome above it
	// shifts by the same amount
	const bottom = (bottomVisible ? coverBottom + 12 : 24) + safeArea.bottom

	let right = 0
	if (rightDock && dockItems.length > 0) {
		const panelOpen = bottomDrawer.isOpen && bottomDrawer.activeTabId !== null
		right = cDockRailWidth + cDockEdgeInset + (panelOpen ? effectiveDockWidth(dockWidth, viewportWidth) + cDockEdgeInset : 0)
	}

	// The phone Layers panel is a sheet over everything, so no inset there
	const left = !isMobile && layersPanelOpen ? cLayersPanelEdgeInset + cLayersPanelWidth + cDockEdgeInset : 0

	return { bottom, right, left, coverBottom }
}

/**
 * Hand the chrome insets to the engine, which centers the globe (fly-to
 * targets, entity focus, crosshair) in the uncovered part of the canvas.
 * The Layers panel is transient, so its `left` is deliberately not
 * published on its own: opening the catalog shouldn't move the globe
 */
export function useViewportInsetsPublisher(): void {
	const { right, coverBottom, left: chromeLeft } = useChromeInsets()
	const isMobile = useIsMobile()
	// The earthquake cutout and storm column HUDs are persistent columns
	// while their block is open, so unlike the Layers panel they do count:
	// the block should be centered in the canvas the HUD leaves visible.
	// Collapsed to its chip, a HUD covers nothing
	const cutoutOpen = useAppSelector(state =>
		(state.dialog.quakeCutout !== null && !state.dialog.quakeCutout.hudCollapsed)
		|| (state.dialog.stormCutout !== null && !state.dialog.stormCutout.hudCollapsed))
	const left = cutoutOpen && !isMobile ? chromeLeft + cLayersPanelEdgeInset + cLayersPanelWidth + cDockEdgeInset : 0
	// Phones: the HUD is a bottom sheet instead, covering as much as the
	// user has it expanded to; the block rises to stay in view above it.
	// Likewise the focused entity's info sheet: fly-to and focus should
	// land the entity in the globe above it, not underneath it
	const cutoutSheetHeight = useAppSelector(state => state.dialog.quakeCutoutSheetHeight)
	const infoSheetHeight = useAppSelector(state => state.dialog.mobileInfoSheetHeight)
	const bottom = Math.max(
		coverBottom,
		cutoutOpen && isMobile ? cutoutSheetHeight : 0,
		isMobile ? infoSheetHeight : 0
	)
	useEffect(() => {
		setViewportInsets({ left, right, top: 0, bottom })
	}, [left, right, bottom])
	useEffect(() => () => setViewportInsets({ left: 0, right: 0, top: 0, bottom: 0 }), [])
}

export function useBottomChromeOffset(): number {
	return useChromeInsets().bottom
}
